import {
  Box,
  Button,
  CircularProgress,
  MenuItem,
  Select,
  TextField,
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import { Checkbox, ColorPicker } from "antd";
import React, { useEffect, useState } from "react";
import {
  addClientToResourceGrid,
  cloneClients,
  deleteClientFromResourceGrid,
  editClientToResourceGrid,
  getClientsFromResourceGrid,
} from "../../../../../api/apiCall";
import { useAuth } from "../../../../../auth/auth";
import { useSearchParams } from "react-router-dom";
import { toast } from "react-toastify";
import styles from "./Clients.module.css";

const Clients = ({ connection, isConnected, hide, setHide }: any) => {
  const { state } = useAuth();
  const [searchParams]: any = useSearchParams();
  const [clients, setClients] = React.useState<any>([]);
  const [selectedRows, setSelectedRows] = React.useState<any>([]);
  const [biggestOrderNumber, setBiggestOrderNumber] = React.useState<any>();
  const [loading, setLoading] = useState(false);
  const [hasClients, setHasClients] = useState(true);
  const currentWeek = Number(searchParams.get("week"));
  const currentYear = Number(searchParams.get("year"));

  const colors = [
    "#F4D6EE",
    "#D5F4EE",
    "#DCF5D7",
    "#D5DDF0",
    "#c1c1f7",
    "#F3D4DA",
    "#FFDBBB",
  ];

  const handleColorChange = (color: any, id: any) => {
    const hexColor = color.toHexString();
    const updatedClients = clients.map((client: any) =>
      client.id === id ? { ...client, color: hexColor } : client
    );
    setClients(updatedClients);
  };

  const handleSelectChange = (event: any, id: any) => {
    const selectedColor = event.target.value;
    const updatedClients = clients.map((client: any) =>
      client.id === id ? { ...client, color: selectedColor } : client
    );
    setClients(updatedClients);
  };

  const handleChange = (id: any, field: any, newValue: any) => {
    const updatedRows = clients.map((row: any) =>
      row.id === id ? { ...row, [field]: newValue } : row
    );
    setClients(updatedRows);
  };

  const handleAddRow = async () => {
    setLoading(true);
    const codeName = `${searchParams.get("week")}-${searchParams.get("year")}`;

    const newRow = {
      id: clients.length + 1,
      clientName: ``,
      endRow: 0,
      startRow: 0,
      color: "#fff",
      codeName: codeName,
    };

    setClients((prevRows: any) => [...prevRows, newRow]);
    const response = await addClientToResourceGrid(state.user?.token, {
      codeName: codeName,
      clientName: newRow.clientName,
      startRow: newRow.startRow,
      endRow: newRow.endRow,
      color: newRow.color,
      orderNumber: biggestOrderNumber + 1,
    });

    if (response) {
      toast.success("Added new client successfully");
      if (connection) {
        await connection.invoke(
          "UpdateClientsFromGrid",
          `${searchParams.get("week")}-${searchParams.get("year")}`
        );
      }
    }
    setLoading(false);
  };

  const cloneClientsFromPreviousWeek = async () => {
    const fromWeek = currentWeek === 1 ? 52 : currentWeek - 1;
    const fromYear = currentWeek === 1 ? currentYear - 1 : currentYear;

    const currentCodeName = `${currentWeek}-${currentYear}`;
    const previousCodeName = `${fromWeek}-${fromYear}`;

    const response = await cloneClients(
      state.user?.token,
      previousCodeName,
      currentCodeName
    );

    if (response?.status === 200) {
      getClients();
      setHasClients(true);

      if (connection) {
        await connection.invoke("UpdateClientsFromGrid", currentCodeName);
      }
    }
  };

  const handleSaveChanges = async () => {
    const response = await editClientToResourceGrid(state.user?.token, clients);
    if (response) {
      toast.success("Updated Clients successfully");
    }
    if (connection) {
      await connection.invoke(
        "UpdateClientsFromGrid",
        `${searchParams.get("week")}-${searchParams.get("year")}`
      );
    }
  };

  const getClients = async () => {
    const response = await getClientsFromResourceGrid(
      state.user?.token,
      `${searchParams.get("week")}-${searchParams.get("year")}`
    );
    if (response) {
      setClients(response);
      if (response.length === 0) setHasClients(false);
      const orderNumbers: [] = response.map(
        (client: any) => client.orderNumber
      );
      const maxOrderNumber =
        orderNumbers && orderNumbers.length > 0 ? Math.max(...orderNumbers) : 0;
      setBiggestOrderNumber(maxOrderNumber);
    }
  };

  const columns: any = [
    {
      field: "orderNumber",
      headerName: "#",
      width: 30,
      filterable: false,
    },
    {
      field: "clientName",
      headerName: "CLIENTS",
      width: 300,
      renderCell: (params: any) => (
        <TextField
          size="small"
          value={params.row.clientName}
          style={{ margin: 5 }}
          fullWidth
          onChange={(event: any) => {
            handleChange(params.id, "clientName", event.target.value);
          }}
          inputProps={{
            ...params.inputProps,
            onKeyDown: (event) => {
              if (event.key === " ") {
                event.stopPropagation();
              }
            },
          }}
        />
      ),
    },
    {
      field: "startRow",
      headerName: "ROW START",
      width: 120,
      renderCell: (params: any) => (
        <TextField
          type="number"
          size="small"
          value={params.row.startRow}
          style={{ width: "90px", height: "5px", margin: 5 }}
          onChange={(event: any) => {
            handleChange(params.id, "startRow", event.target.value);
          }}
        />
      ),
    },
    {
      field: "endRow",
      headerName: "ROW END",
      width: 120,
      align: "center",
      renderCell: (params: any) => (
        <TextField
          type="number"
          size="small"
          value={params.row.endRow}
          style={{ width: "90px", height: "5px", margin: 5 }}
          onChange={(event: any) => {
            handleChange(params.id, "endRow", event.target.value);
          }}
        />
      ),
    },
    {
      field: "color",
      headerName: "COLOR",
      width: 200,
      renderCell: (params: any) => (
        <div style={{ display: "flex", alignItems: "center" }}>
          <Select
            labelId="demo-select-small-label"
            id="demo-select-small"
            style={{
              height: "28px",
              width: "50px",
              backgroundColor: params.row.color,
            }}
            defaultValue={params.row.color}
            onChange={(event) => handleSelectChange(event, params.row.id)}
          >
            {colors.map((options: any) => (
              <MenuItem
                value={options}
                style={{ height: 40, backgroundColor: options }}
              />
            ))}
          </Select>
          <ColorPicker
            style={{ fontSize: 20, margin: 10 }}
            showText
            value={params.row.color}
            onChange={(color: any) => handleColorChange(color, params.row.id)}
          />
        </div>
      ),
    },
  ];

  const handleRowSelection = (newSelectionModel: any) => {
    setSelectedRows(newSelectionModel);
  };

  const deleteWorker = async () => {
    const response = await deleteClientFromResourceGrid(
      state.user?.token,
      selectedRows
    );
    if (response) {
      toast.error("Deleted client successfully");
      if (connection) {
        await connection.invoke(
          "UpdateClientsFromGrid",
          `${searchParams.get("week")}-${searchParams.get("year")}`
        );
      }
    }
  };

  useEffect(() => {
    getClients();
  }, []);

  useEffect(() => {
    getClients();
  }, [isConnected]);

  useEffect(() => {
    if (connection) {
      connection.on("UpdateClientsFromGrid", () => {
        getClients();
      });
      return () => {
        connection.off("UpdateClientsFromGrid");
      };
    }
  }, [connection]);

  return (
    <div>
      <h2 className={styles.clients}>Clients</h2>
      <Box height={400} width={"95%"} margin={"0 auto"}>
        <DataGrid
          rows={clients}
          columns={columns}
          checkboxSelection
          disableRowSelectionOnClick
          onRowSelectionModelChange={handleRowSelection}
          sx={{
            "& .MuiDataGrid-columnHeaderTitle": {
              fontWeight: "bold",
            },
          }}
        />
      </Box>
      <div className={styles.buttonsContainer}>
        <div className={styles.margin10}>
          <Button
            variant="contained"
            onClick={handleSaveChanges}
            style={{
              margin: "5px",
              color: "white",
              borderColor: "black",
              height: "35px",
              boxShadow: "none",
            }}
          >
            Save Changes
          </Button>
          <Button
            variant="outlined"
            onClick={handleAddRow}
            disabled={loading}
            style={{
              margin: "5px",
              color: "black",
              borderColor: "#1976d2",
              height: "35px",
              boxShadow: "none",
              width: 135,
            }}
          >
            {loading ? (
              <CircularProgress size={25} style={{color: "#1976d2"}} />
            ) : "Add a Client"}
          </Button>
          &nbsp;&nbsp;Freez Clients Panel: &nbsp;&nbsp;
          <Checkbox
            checked={hide}
            style={{color: hide && "#74c365", transform: "scale(1.4)"}}
            onChange={(checked: any) => setHide(!hide)}
          />
        </div>
        <div>
          {!hasClients && (
            <Button
              variant="contained"
              onClick={cloneClientsFromPreviousWeek}
              disabled={loading}
              style={{
                margin: "20px",
                color: "white",
                borderColor: "black",
                height: "35px",
                boxShadow: "none",
              }}
            >
              {loading ? (
                <CircularProgress size={25} style={{ color: "white" }} />
              ) : currentWeek === 1 ? (
                "Clone Week  52"
              ) : (
                `Clone Week ${currentWeek - 1}`
              )}
            </Button>
          )}
          {selectedRows && selectedRows.length > 0 && (
            <Button
              onClick={deleteWorker}
              style={{
                margin: "20px",
                color: "white",
                backgroundColor: "red",
                height: "35px",
                boxShadow: "none",
                alignItems: "right",
              }}
            >
              Delete
            </Button>
          )}
        </div>
      </div>
    </div>
  );
};

export default Clients;
